// src/pages/PaymentPlans.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CheckIcon from "@mui/icons-material/Check";
import SavingsIcon from "@mui/icons-material/Savings";
import PublicIcon from "@mui/icons-material/Public";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

/* ---------- assets ---------- */
import heroImg from "../assets/solar-installation.jpg";
import plan1Img from "../assets/solar-farm.jpg";
import plan2Img from "../assets/eco-home.jpg";
import plan3Img from "../assets/solar-team.jpg";
import benefitImg from "../assets/solar-roof.jpg";

/* ---------- constants ---------- */
const ORANGE = "#E06A00";

/* ---------- dummy data ---------- */
const PLANS = [
  {
    img: plan1Img,
    name: "Plan Ahorro",
    price: "$ 999",
    subtitle: "Hasta 5 kWp • Hogar eficiente",
    features: ["Ahorra hasta 60 %", "Paneles premium", "Garantía 10 años"],
  },
  {
    img: plan2Img,
    name: "Plan Plus",
    price: "$ 1 799",
    subtitle: "Hasta 10 kWp • PyME sostenible",
    features: ["Ahorra hasta 80 %", "Monitoreo 24/7", "Garantía 15 años"],
  },
  {
    img: plan3Img,
    name: "Plan Premium",
    price: "$ 3 299",
    subtitle: "15 kWp+ • Industria verde",
    features: ["Ahorra hasta 95 %", "Diseño a la medida", "Garantía 25 años"],
  },
];

const BENEFITS = [
  {
    icon: SavingsIcon,
    title: "Máximo ahorro",
    desc: "Factura mínima desde el primer mes.",
  },
  { icon: PublicIcon, title: "Menos CO₂", desc: "Hasta 10 t evitadas / año." },
  {
    icon: SecurityIcon,
    title: "Garantía real",
    desc: "Componentes certificados.",
  },
  {
    icon: SupportAgentIcon,
    title: "Soporte 24/7",
    desc: "Asistencia permanente.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Agenda asesoría gratuita",
    desc: "Calculamos tu ahorro real.",
  },
  {
    n: "2",
    title: "Instalación en 72 h",
    desc: "Equipo certificado sin interrupciones.",
  },
  {
    n: "3",
    title: "Ahorra y cuida el planeta",
    desc: "Monitorea tu sistema y disfruta.",
  },
];

/* ---------- helpers ---------- */
const fade = {
  hidden: { opacity: 0, y: 32 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: d, duration: 0.55, ease: "easeOut" },
  }),
};

const slide = {
  enter: (dir) => ({ x: dir > 0 ? 640 : -640, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -640 : 640, opacity: 0 }),
};

export default function PaymentPlans() {
  const theme = useTheme();

  /* ---------- carrusel ---------- */
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(0);

  /* altura dinámica del wrapper */
  const [wrapperH, setWrapperH] = useState(500);
  const slideRef = useRef(null);

  useEffect(() => {
    if (!slideRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        // 24 px de holgura para radio + sombra
        setWrapperH(e.contentRect.height + 24);
      }
    });
    ro.observe(slideRef.current);
    return () => ro.disconnect();
  }, [index]);

  const prev = () => {
    setDir(-1);
    setIndex((p) => (p === 0 ? PLANS.length - 1 : p - 1));
  };
  const next = () => {
    setDir(1);
    setIndex((p) => (p === PLANS.length - 1 ? 0 : p + 1));
  };

  return (
    <Box component="main">
      {/* HERO */}
      <Box
        sx={{
          minHeight: { xs: 280, md: 440 },
          background: `linear-gradient(rgba(0,0,0,.55),rgba(0,0,0,.55)),url(${heroImg}) center/cover`,
          display: "flex",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <Container>
          <Typography
            component={motion.h1}
            variants={fade}
            initial="hidden"
            animate="visible"
            fontSize={{ xs: 30, md: 48 }}
            fontWeight={900}
            lineHeight={1.15}
            mb={2}
          >
            Ahorra energía.
            <br />
            Ahorra dinero.
          </Typography>
          <Typography
            component={motion.p}
            variants={fade}
            animate="visible"
            custom={0.1}
            maxWidth={600}
            mb={4}
          >
            Reduce hasta el 95&nbsp;% tu factura eléctrica con energía solar.
            ¡La inversión se paga sola y ayudas al medio ambiente!
          </Typography>
          <motion.div variants={fade} animate="visible" custom={0.2}>
            <Button
              component={NavLink}
              to="/cotizar"
              variant="contained"
              size="large"
              sx={{
                bgcolor: ORANGE,
                fontWeight: 700,
                px: 4,
                textTransform: "none",
                "&:hover": { bgcolor: "#c75a00" },
              }}
            >
              Cotizar ahora
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* PLANS */}
      <Box sx={{ py: { xs: 12, md: 16 }, bgcolor: "#f6f8fa" }}>
        <Container maxWidth="md" sx={{ position: "relative" }}>
          <Typography
            variant="h4"
            fontWeight={900}
            align="center"
            mb={10}
            color="text.primary"
            component={motion.div}
            variants={fade}
            initial="hidden"
            whileInView="visible"
          >
            Elige tu paquete ideal
          </Typography>

          {/* Flechas */}
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              top: "50%",
              left: { xs: -4, sm: -72 },
              transform: "translateY(-50%)",
              color: ORANGE,
              fontSize: 34,
              "&:hover": { scale: "1.15" },
            }}
          >
            <ArrowBackIosNewIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              top: "50%",
              right: { xs: -4, sm: -72 },
              transform: "translateY(-50%)",
              color: ORANGE,
              fontSize: 34,
              "&:hover": { scale: "1.15" },
            }}
          >
            <ArrowForwardIosIcon fontSize="inherit" />
          </IconButton>

          {/* Wrapper con alto dinámico */}
          <Box
            sx={{
              position: "relative",
              height: wrapperH,
              transition: "height .3s ease",
              overflowX: "hidden",
              overflowY: "visible",
            }}
          >
            <AnimatePresence custom={dir} initial={false}>
              <motion.div
                key={index}
                custom={dir}
                variants={slide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 80, damping: 18 },
                  opacity: { duration: 0.25 },
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
                ref={slideRef}
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: 720, md: 960 },
                    bgcolor: "#fff",
                    borderRadius: 6,
                    boxShadow: 4,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    overflow: "hidden",
                  }}
                >
                  {/* Imagen 38 % */}
                  <Box
                    component="img"
                    src={PLANS[index].img}
                    alt={PLANS[index].name}
                    sx={{
                      width: { xs: "100%", md: "38%" },
                      height: { xs: 220, md: "100%" },
                      objectFit: "cover",
                    }}
                  />

                  {/* Info */}
                  <Box
                    sx={{
                      flex: 1,
                      p: { xs: 4, md: 5 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h5" fontWeight={900} mb={0.5}>
                      {PLANS[index].name}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      mb={2}
                    >
                      {PLANS[index].subtitle}
                    </Typography>

                    <Typography
                      variant="h4"
                      fontWeight={900}
                      color={ORANGE}
                      mb={3}
                    >
                      {PLANS[index].price}
                    </Typography>

                    <List dense sx={{ mb: 4 }}>
                      {PLANS[index].features.map((f) => (
                        <ListItem key={f} disableGutters>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon sx={{ color: ORANGE }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={f}
                            primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      component={NavLink}
                      to="/cotizar"
                      variant="contained"
                      sx={{
                        alignSelf: "flex-start",
                        bgcolor: ORANGE,
                        color: "#fff",
                        fontWeight: 700,
                        px: 5,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#c75a00" },
                      }}
                    >
                      Seleccionar
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Container>
      </Box>

      {/* BENEFITS */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 6,
              alignItems: "center",
            }}
          >
            <motion.img
              src={benefitImg}
              alt="Casa con paneles"
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{
                flex: "1 1 55%",
                borderRadius: 14,
                boxShadow: theme.shadows[3],
                maxWidth: "55%",
              }}
            />

            <Box flex="1 1 320px">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <motion.div
                    key={b.title}
                    variants={fade}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i * 0.15}
                    style={{ marginBottom: 24 }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Avatar sx={{ bgcolor: ORANGE }}>
                        <Icon />
                      </Avatar>
                      <Box>
                        <Typography fontWeight={700}>{b.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {b.desc}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* STEPS */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f6f8fa" }}>
        <Container maxWidth="md">
          <Typography
            variant="h4"
            fontWeight={900}
            align="center"
            mb={8}
            component={motion.div}
            variants={fade}
            initial="hidden"
            whileInView="visible"
          >
            Así de fácil es empezar
          </Typography>

          <Box sx={{ ml: 3, position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                left: 32,
                top: 48,
                bottom: 0,
                width: 4,
                bgcolor: "rgba(224,106,0,.15)",
                borderRadius: 2,
                display: { xs: "none", md: "block" },
              }}
            />
            {STEPS.map((s, idx) => (
              <motion.div
                key={s.n}
                variants={fade}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={idx * 0.2}
                style={{ marginBottom: 40 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: { md: idx * 5 },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: ORANGE,
                      width: 64,
                      height: 64,
                      fontSize: 28,
                      fontWeight: 700,
                      mr: 3,
                    }}
                  >
                    {s.n}
                  </Avatar>
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 3,
                      boxShadow: 1,
                      p: 2,
                      width: "100%",
                      maxWidth: 380,
                    }}
                  >
                    <Typography fontWeight={700}>{s.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.desc}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA ORANGE */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: ORANGE,
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={900} mb={2}>
            ¡Empieza a ahorrar desde hoy!
          </Typography>
          <Typography variant="body1" mb={4}>
            Únete a los hogares y empresas que ya reducen costos y huella de
            carbono con KWENERGY.
          </Typography>
          <Button
            component={NavLink}
            to="/cotizar"
            variant="contained"
            size="large"
            sx={{
              bgcolor: "#fff",
              color: ORANGE,
              fontWeight: 700,
              px: 5,
              textTransform: "none",
              "&:hover": { bgcolor: "#f0f0f0" },
            }}
          >
            Cotizar ahora
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
